/**
 * storeIdResolver.ts
 *
 * Pure async utility — NO 'use client' directive.
 * Safe to import from both React components and plain service files.
 *
 * Contains the module-level cache and resolution logic for store_id.
 * The React hook (useStoreId.ts) imports from here.
 */
import { createClient } from '@/lib/supabase/client';

let _cachedStoreId: string | null = null;
let _resolving: Promise<string | null> | null = null;
let _impersonatedStoreId: string | null = null;

export function setImpersonatedStoreId(id: string | null): void {
  _cachedStoreId = null;
  _resolving = null;
  _impersonatedStoreId = id;
}

export async function resolveStoreId(bypassCache = false): Promise<string | null> {
  if (_impersonatedStoreId !== null) return _impersonatedStoreId;
  if (!bypassCache && _cachedStoreId !== null) return _cachedStoreId;
  if (bypassCache) {
    _cachedStoreId = null;
    _resolving = null;
  }
  if (_resolving) return _resolving;

  _resolving = (async () => {
    try {
      // 1. Sub-user session stored in localStorage (highest priority)
      if (typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem('ef_subuser_session');
          if (raw) {
            const subUser = JSON.parse(raw);
            if (subUser?.storeId) {
              _cachedStoreId = subUser.storeId as string;
              return _cachedStoreId;
            }
          }
        } catch {
          // ignore
        }
      }

      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // 2. Look up store_id from ef_app_users by auth_user_id (UUID) — RLS is disabled on this table
      const { data: byUid, error: uidError } = await supabase
        .from('ef_app_users')
        .select('store_id')
        .eq('auth_user_id', user.id)
        .not('store_id', 'is', null)
        .maybeSingle();
      if (!uidError && byUid?.store_id) {
        _cachedStoreId = byUid.store_id as string;
        return _cachedStoreId;
      }

      // 3. Fallback: look up by email in ef_app_users (covers rows not yet backfilled with auth_user_id)
      if (user.email) {
        const { data: byEmail, error: emailError } = await supabase
          .from('ef_app_users')
          .select('store_id')
          .eq('email', user.email)
          .not('store_id', 'is', null)
          .maybeSingle();
        if (!emailError && byEmail?.store_id) {
          _cachedStoreId = byEmail.store_id as string;
          return _cachedStoreId;
        }
      }

      // 4. Direct ef_stores lookup by auth_user_id (RLS disabled on ef_stores)
      const { data: storeByUid, error: storeUidError } = await supabase
        .from('ef_stores')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      if (!storeUidError && storeByUid?.id) {
        _cachedStoreId = storeByUid.id as string;
        return _cachedStoreId;
      }

      // 5. Last resort: ef_stores lookup by owner_email
      if (user.email) {
        const { data: storeByEmail, error: storeEmailError } = await supabase
          .from('ef_stores')
          .select('id')
          .eq('owner_email', user.email)
          .maybeSingle();
        if (!storeEmailError && storeByEmail?.id) {
          _cachedStoreId = storeByEmail.id as string;
          return _cachedStoreId;
        }
      }

      // Do NOT cache null — allow retry on next call
      return null;
    } finally {
      _resolving = null;
    }
  })();

  return _resolving;
}

export function clearStoreIdCache(): void {
  _cachedStoreId = null;
  _resolving = null;
  _impersonatedStoreId = null;
}

export function getImpersonatedStoreId(): string | null {
  return _impersonatedStoreId;
}

export function getCachedStoreId(): string | null {
  return _cachedStoreId;
}
